import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { mergeApi, errMsg } from "../institutionMerge.api";

const { Text, Paragraph } = Typography;

/**
 * The restore gate. Blocked by default; unlocks only once every bucket is answered:
 *   1. revertible — replays cleanly
 *   2. drift — changed since the merge, left untouched
 *   3. new on target — re-link to a source, or keep the target alive for them
 *   4. entangled — blocking (draft bill spanning old+new) or overridable (closed)
 */
export function MergeRestore({ merge, onDone }) {
  const [rp, setRp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [relink, setRelink] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [keepTarget, setKeepTarget] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const sources = merge.sourceInstitutionIds || [];
  // merged into an institution that existed before: it is never deleted, only the sources' records leave it
  const existing = merge.targetMode === "existing";
  const targetName = (merge.targetInstitutionId?.name || "").trim();
  // no target name to type (target gone / never named): the merge label confirms instead
  const confirmWord = targetName || (merge.label || "").trim();
  const detachedCount = (merge.detached || []).length;

  // re-analysed whenever the merge changes (a detach moves records out of the restore's scope)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await mergeApi.restorePreview(merge._id);
        setRp(d);
        const pre = {};
        for (const n of d.newOnTarget)
          if (n.suggestedSource) pre[n.documentId] = n.suggestedSource._id;
        setRelink(pre);
      } catch (e) {
        notification.error({ message: "Erreur", description: errMsg(e) });
      } finally {
        setLoading(false);
      }
    })();
  }, [merge._id, detachedCount, merge.updatedAt]);

  if (!rp)
    return (
      <Alert
        type="info"
        message={loading ? "Analyse en cours…" : "Analyse indisponible"}
      />
    );

  const unhandledNew = rp.newOnTarget.filter(n => !relink[n.documentId]);
  const unacked = rp.entangled.warnings.filter(
    w => !overrides.includes(w.documentId),
  );
  const blocked = rp.entangled.blocking.length > 0;
  const ready =
    !blocked &&
    unacked.length === 0 &&
    (unhandledNew.length === 0 || keepTarget || existing) &&
    !!confirmWord &&
    confirm.trim() === confirmWord;

  const run = async () => {
    setLoading(true);
    try {
      const m = await mergeApi.restore(merge._id, {
        // an existing target always stays: what nobody relinked simply remains on it
        keepTarget: existing || keepTarget,
        overrides,
        relink,
        reason: reason.trim() || undefined,
      });
      notification.success({
        message: "Fusion restaurée",
        description: `${m.restore.reverted} enregistrement(s) remis en place`,
        duration: 8,
      });
      onDone();
    } catch (e) {
      notification.error({
        message: "Restauration refusée",
        description: errMsg(e),
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="warning"
        showIcon
        message="La restauration fonctionne à tout moment — mais plus tard, plus de décisions vous reviennent."
        description="Tout ce que la fusion a déplacé revient grâce au journal, quel que soit le délai. Ce qui a été créé ou modifié depuis (catégories 2 à 4 ci-dessous) n'appartenait à aucun ancien établissement : la restauration vous demande où le rattacher, ou le laisse sur l'institution résultante, qui est alors conservée. Une sauvegarde de la base n'est pas un moyen de restaurer : elle effacerait tout le travail fait depuis la fusion."
      />

      <Space size="large" wrap>
        <Statistic title="1 · Réversibles" value={rp.revertible} />
        <Statistic
          title="2 · Modifiés depuis"
          value={rp.driftTotal}
          valueStyle={{ color: rp.driftTotal ? "#d48806" : undefined }}
        />
        <Statistic
          title="3 · Créés après la fusion"
          value={rp.newOnTargetTotal}
          valueStyle={{ color: rp.newOnTargetTotal ? "#d48806" : undefined }}
        />
        <Statistic
          title="4 · À cheval (bloquants)"
          value={rp.entangled.blocking.length}
          valueStyle={{ color: blocked ? "#cf1322" : undefined }}
        />
        <Statistic
          title="4 · À cheval (avertissements)"
          value={rp.entangled.warnings.length}
        />
      </Space>

      {rp.driftTotal > 0 && (
        <div>
          <Text strong>2 · Modifiés depuis la fusion — laissés tels quels</Text>
          <Paragraph type="secondary">
            Quelqu'un a déplacé ces enregistrements vers une autre institution
            après la fusion. La restauration ne les touche pas, pour ne pas
            écraser un choix délibéré.
          </Paragraph>
          <Table
            size="small"
            rowKey={r => `${r.model}:${r.documentId}:${r.field}`}
            dataSource={rp.drift}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: "Collection", dataIndex: "model" },
              {
                title: "Enregistrement",
                dataIndex: "label",
                render: x => x || <Text type="secondary">—</Text>,
              },
              { title: "Champ", dataIndex: "field" },
              {
                title: "Aujourd'hui",
                dataIndex: "currentLabel",
                render: x => x || <Text type="secondary">—</Text>,
              },
              {
                title: "ID",
                dataIndex: "documentId",
                render: v => (
                  <Text copyable={{ text: String(v) }}>
                    …{String(v).slice(-8)}
                  </Text>
                ),
              },
            ]}
          />
        </div>
      )}

      {rp.newOnTargetTotal > 0 && (
        <div>
          <Text strong>
            3 · Créés après la fusion — nulle part où « revenir »
          </Text>
          <Paragraph type="secondary">
            Ces enregistrements n'ont jamais appartenu à un ancien
            établissement. Rattachez chacun à la source suggérée (déduite du
            demandeur, du créateur ou de l'adresse de facturation),
            choisissez-en une autre, ou laissez vide
            {existing
              ? " : ils restent sur l'institution cible, qui existait avant la fusion et est conservée."
              : " et gardez la cible en vie pour eux."}
          </Paragraph>
          <Table
            size="small"
            rowKey="documentId"
            dataSource={rp.newOnTarget}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: "Collection", dataIndex: "model" },
              { title: "Enregistrement", dataIndex: "label" },
              {
                title: "Rattacher à",
                render: (_, n) => (
                  <Select
                    allowClear
                    style={{ width: 300 }}
                    placeholder={
                      n.suggestedSource
                        ? `suggéré : ${n.suggestedSource.name}`
                        : "aucune suggestion"
                    }
                    value={relink[n.documentId]}
                    onChange={v =>
                      setRelink(r => ({ ...r, [n.documentId]: v }))
                    }
                    options={sources.map(s => ({
                      value: s._id,
                      label: s.name,
                    }))}
                  />
                ),
              },
            ]}
          />
          {!existing && (
            <Checkbox
              style={{ marginTop: 8 }}
              checked={keepTarget}
              onChange={e => setKeepTarget(e.target.checked)}
            >
              Garder l'institution cible en vie pour les {unhandledNew.length}{" "}
              enregistrement(s) non rattaché(s) — elle sera marquée comme résidu
              de cette fusion
            </Checkbox>
          )}
        </div>
      )}

      {(rp.entangled.blocking.length > 0 ||
        rp.entangled.warnings.length > 0) && (
        <div>
          <Text strong>
            4 · À cheval — factures/lots couvrant des prestations migrées ET
            nouvelles
          </Text>
          {blocked && (
            <Alert
              type="error"
              showIcon
              style={{ margin: "8px 0" }}
              message="Bloqué."
              description="Une facture brouillon non exportée couvre des prestations des deux côtés. La facturation groupée la recalculerait et pourrait la supprimer. Annulez-la et réémettez-la dans l'application, puis relancez l'analyse."
            />
          )}
          <Table
            size="small"
            rowKey="documentId"
            dataSource={[...rp.entangled.blocking, ...rp.entangled.warnings]}
            pagination={false}
            columns={[
              { title: "Collection", dataIndex: "model" },
              { title: "N°", dataIndex: "label" },
              {
                title: "Statut",
                dataIndex: "status",
                render: (s, r) => (
                  <Space>
                    {s || "—"}
                    {r.exported && <Tag>exportée</Tag>}
                  </Space>
                ),
              },
              {
                title: "Migrées / nouvelles",
                render: (_, r) => `${r.migrated} / ${r.fresh}`,
              },
              {
                title: "Gravité",
                dataIndex: "severity",
                render: s =>
                  s === "blocking" ? (
                    <Tag color="red">bloquant</Tag>
                  ) : (
                    <Tag color="orange">avertissement</Tag>
                  ),
              },
              {
                title: "Acquitter",
                render: (_, r) =>
                  r.severity === "warning" ? (
                    <Checkbox
                      checked={overrides.includes(r.documentId)}
                      onChange={e =>
                        setOverrides(o =>
                          e.target.checked
                            ? [...o, r.documentId]
                            : o.filter(x => x !== r.documentId),
                        )
                      }
                    >
                      je prends la responsabilité
                    </Checkbox>
                  ) : null,
              },
            ]}
          />
        </div>
      )}

      <div>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>Raison de la restauration</Text>{" "}
          <Text type="secondary">
            (facultatif — conservée dans l'historique)
          </Text>
        </Paragraph>
        <Input.TextArea
          rows={2}
          style={{ maxWidth: 640, marginBottom: 16 }}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="ex. établissement sélectionné par erreur, demande du client…"
        />
        <Paragraph>
          {targetName
            ? "Pour confirmer, saisissez le nom de l'institution cible : "
            : "Pour confirmer, saisissez le libellé de la fusion : "}
          <Text code>{confirmWord}</Text>
        </Paragraph>
        <Space>
          <Input
            style={{ width: 360 }}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
          />
          <Button
            danger
            type="primary"
            size="large"
            disabled={!ready}
            loading={loading}
            onClick={() =>
              Modal.confirm({
                title: "Restaurer la fusion ?",
                okType: "danger",
                okText: "Restaurer",
                content: `${
                  rp.revertible
                } enregistrement(s) reviennent aux anciens établissements, qui sont désarchivés. ${
                  existing
                    ? "L'institution cible existait avant la fusion : elle est conservée, seuls les enregistrements des établissements la quittent."
                    : keepTarget
                    ? "La cible est conservée (résidu de fusion)."
                    : "La cible est supprimée."
                }`,
                onOk: run,
              })
            }
          >
            Restaurer
          </Button>
        </Space>
        {!ready && !blocked && (
          <Paragraph type="secondary" style={{ marginTop: 8 }}>
            {unacked.length
              ? `${unacked.length} avertissement(s) à acquitter. `
              : ""}
            {unhandledNew.length && !keepTarget && !existing
              ? `${unhandledNew.length} enregistrement(s) à rattacher ou cocher « garder la cible ». `
              : ""}
            {confirm.trim() !== confirmWord
              ? targetName
                ? "Saisissez le nom exact."
                : "Saisissez le libellé exact."
              : ""}
          </Paragraph>
        )}
      </div>
    </Space>
  );
}
